import React, { useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
} from "@mui/material";
import { viruses } from "@prisma/client";
import Image from "next/image";

interface contextOption {
  displayedValue: string;
  value: viruses;
  icon: string;
  color: string;
  textColor: string;
  // The name of the JSON file used by Auspice:
  // ${auspiceJsonName}_${auspiceJsonSerotypePrefix}${serotype}.json
  // ex: dengue_DENV2.json
  auspiceJsonName: string;
  auspiceJsonSerotypePrefix: string;
}

export const useVirusOptions = () => {
  const [virusOptions, setVirusOptions] = useState<contextOption[]>([]);

  useEffect(() => {
    setVirusOptions([
      {
        displayedValue: "Dengue",
        value: viruses.DENV,
        icon: "/assets/logo/icon_DENV.svg",
        // The colour is also used in the documentation. Do not forget to also
        // update it there.
        color: "#d21e66",
        textColor: "white",
        // Used by Auspice from Emergen to build the dataset JSON file name.
        //
        // Structure:
        //    ${auspiceJsonName}_${auspiceJsonSerotypePrefix}${serotype}.json
        //
        // Ex with dengue serotype DENV-2:
        //    dengue_DENV2.json
        auspiceJsonName: "dengue",
        auspiceJsonSerotypePrefix: "DENV",
      },
      {
        displayedValue: "Chikungunya",
        value: viruses.CHIKV,
        icon: "/assets/logo/icon_CHIKV.svg",
        // The colour is also used in the documentation. Do not forget to also
        // update it there.
        color: "#ee621e",
        textColor: "white",
        auspiceJsonName: "chikungunya",
        auspiceJsonSerotypePrefix: "",
      },
      // {
      //   displayedValue: "West Nile",
      //   value: viruses.WNV,
      //   icon: "/assets/logo/icon_WNV.svg",
      //   color: "#1DB2D1",
      //   textColor: "white",
      // },
    ]);
  }, []);

  return virusOptions;
};

const VirusSelector = () => {
  const theme = useTheme();
  const [selectedVirus, setSelectedVirus] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const virusOptions = useVirusOptions();

  useEffect(() => {
    if (!dialogOpen) {
      setSelectedVirus("");
    }
  }, [dialogOpen]);

  const handleSelect = (event: any) => {
    console.log("handleSelect", event.target.value);
    setSelectedVirus(event.target.value as string);
    setDialogOpen(true);
  };

  const handleChange = () => {
    const newContext = virusOptions.find(
      (virus) => virus.displayedValue === selectedVirus,
    )?.value;
    if (newContext) {
      const url = new URL(window.location.href);
      url.searchParams.set("context", newContext);
      window.open(url.toString(), "_blank");
    }
    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    console.log("handleCloseDialog");
    setDialogOpen(false);
  };

  return (
    <Box>
      <Select
        value={selectedVirus}
        onChange={handleSelect}
        displayEmpty
        renderValue={() => "Virus Species"}
        MenuProps={{
          PaperProps: {
            sx: {
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "8px",
              padding: "8px",
              backgroundColor: theme.palette.primary.main,
              borderRadius: "8px",
              width: "fit-content",
              minWidth: "300px",
              transform: "translateX(-150px)",
              "& ul": {
                display: "contents",
              },
            },
          },
        }}
        sx={{
          bgcolor: theme.palette.success.contrastText,
          fontWeight: "bold",
          color: theme.palette.primary.contrastText,
          "& .MuiSelect-icon": { color: theme.palette.primary.contrastText },
        }}
      >
        {virusOptions.map((virus) => (
          <MenuItem
            key={virus.value}
            value={virus.displayedValue}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100px",
              padding: "12px",
              color: "white",
              borderRadius: "8px",
            }}
          >
            <Image
              src={virus.icon}
              alt={virus.displayedValue}
              width={60}
              height={40}
              style={{ borderRadius: "10px" }}
            />

            <Typography variant="body2" sx={{ paddingTop: "8px" }}>
              {virus.displayedValue}
            </Typography>
          </MenuItem>
        ))}
      </Select>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "10px",
            border: "3px solid",
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ color: theme.palette.primary.main }}
        >
          {"CHANGE VIRUS SPECIES TO " + selectedVirus}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You will be redirected to a new tab. This session will remain
            active.
            <br />
            <br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseDialog}>
            No
          </Button>
          <Button variant="contained" onClick={handleChange} autoFocus>
            Yes, open tab
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VirusSelector;
